<!--
  -  Copyright (c) Qwoach LLC 2021. All rights reserved.
  -  Unauthorized copying of this file, via any medium is strictly prohibited
  -  Proprietary and confidential
  -  Created by Artem Bogush <artem@qwoach.com> on 4/1/2021
  -->

<template>
	<div class="program-invite-panel-root qwoach-form relative">
		<PanelTitle :tooltipAnalyticsProps="{label:'Start coaching', ...analyticsPropsMethod()}">
			<div style="font-weight: bold">Start coaching</div>
			<template></template>
			<template #tooltip>
				You can reuse this program template multiple times to coach different clients.
				<br/>
				<br/>
				To starting coaching, just copy the invitation link below and send it to your client.
				<br/>
				<br/>
				Qwoach will copy this program template to start a coaching process for each specific client. You'll be able to further adjust the coaching process as needed for each client individually.
				<br/>
				<br/>
				A started coaching process for each client will appear under "Coaching" -> "My coaching".
			</template>
		</PanelTitle>

		<div v-if="requireSetupItems.length">
			<div class="text-warning">To invite clients and start coaching please finish setting up this program first.</div>
			<div class="mt-4 text-muted">Items that require setup:</div>
			<div v-for="(item, index) of requireSetupItems" :key="index">
				<small class="text-muted">
					- <span v-if="item.link"><router-link :to="item.link">{{item.title}}</router-link></span>
					<span v-else>{{item.title}}</span>
				</small>
			</div>
		</div>

		<div v-else>
			<FormRow tooltip-bubble-position="bottom"
			:tooltip-analytics-props="{label:'Copy invitation link', ...analyticsPropsMethod()}">
				<div class="row mx-0 align-items-center">
					<div>
						<qw-link-button v-if="programItem.isLinkInviteEnabled"
										@click="copyInvitationLink()">
							Copy invitation link
						</qw-link-button>
						<span v-else>
						Invitation link is disabled
					</span>
					</div>
					<div class="layout-spacer"></div>
					<el-switch v-model="programItem.isLinkInviteEnabled" @change="onInvitationLinkEnableChanged"/>

				</div>
				<template #tooltip>
					Copy the link and send it to your client so they can get access to this program.
					<br/>Or put the invitation link on your website so your clients can sign up on their own.
					<br/>
					<br/>
					You can disable the link using the switch on the left to prevent further sign ups to this program.
					<br/>
					<br/>
					Tip: For each client who signs up using the link, Qwoach will create a coaching process based on this program template under "Coaching" -> "My Coaching"
				</template>
			</FormRow>

			<FormRow
				:tooltip-analytics-props="{label:'Invite a client', ...analyticsPropsMethod()}">
				<qw-link-button @click="inviteClientClicked">
					Invite a client
				</qw-link-button>
				<template #tooltip>
					You can invite a specific person by email.
					<br/>
					<br/>
					Just put their email and Qwoach will create a coaching process under "Coaching" -> "My Coaching" for that person.
					<br/>
					<br/>
					The person will get an invitation to their inbox so they can join the coaching.
					<br/>
					<br/>
					Qwoach will also create a client record for you automatically if a person with such email doesn't exist yet.
				</template>
			</FormRow>
		</div>



		<LoadingIndicator/>
	</div>
</template>

<script>
import BaseComponent from "@/core/components/BaseComponent.vue";
import FormRow from "@/core/components/Form/Base/FormRow.vue";
import CoachingProgram from "@shared/Program/CoachingProgram";
import LoadingIndicator from "@/core/components/LoadingIndicator.vue";
import PanelTitle from "@/core/modules/coach/Coaching/Panels/PanelTitle.vue";
import {createDialogLazy} from "@/utils/helpers-external";
let InviteClientDialog = () => import("./InviteClientDialog")
import {yesNoDialogOld} from "@/core/components/Dialogs/dialog";

let inviteClientDialog = createDialogLazy(InviteClientDialog)

export default {
	name: "ProgramInvitePanel",
	components: {PanelTitle, LoadingIndicator, FormRow},
	extends: BaseComponent,
	props: {
		programItem: {CoachingProgram, required: true},
		disabled: {Boolean, default: false},
		requireSetupItems: {Array, default: []},
		analyticsProps: Object
	},

	methods: {

		async inviteClientClicked(){
			let _ = this.$analyticManager.trackEvent("Invite client clicked", this.analyticsPropsMethod())
			let {programItem, clientProfile} = await inviteClientDialog({
				actionOk: async (clientProfile, dialog) => this.createProcessAndSendInvite(clientProfile, dialog)
			})
			if(!programItem){
				return
			}
			let res = await yesNoDialogOld("Invite sent successfully", `The invite was sent and a new coaching process for ${clientProfile.name} was created under "Coaching" -> "My Coaching"<br/><br/>Do you want to open the new coaching process to further adjust it specifically for the client?`, "Yes, open coaching", "Cancel")
			if(res){
				// await this.go("ProcessDetailsCoach", {programId: programItem.id})
				await this.go("CoachingMainCoach", {category: "processes"})
			}
		},

		async createProcessAndSendInvite(clientProfile, dialog){
			dialog.progressText = "Preparing coaching program..."

			clientProfile = await this.ensureClientProfileExists(clientProfile)

			let programItem = await this.$dataManager.apiPostStartCoachingProcess({
				programId: this.programItem.id,
				participantsLimit: 1,
				allowedParticipantsEmails: [clientProfile.email]
			})

			dialog.progressText = "Sending invite..."

			await this.$dataManager.apiPostSendInvite({
				clientProfileId: clientProfile.id,
				programId: programItem.id
			});

			return {programItem, clientProfile}

		},

		async ensureClientProfileExists(clientProfile){
			let existingClientProfile = await this.$dataManager.findClientProfileByEmail(clientProfile.email)
			if(!existingClientProfile){
				clientProfile = await this.$dataManager.apiPostCreateClientProfile({coachId: this.ownerProfile.id, clientProfile})
			}
			return existingClientProfile || clientProfile
		},


		async copyInvitationLink() {
			let _ = this.$analyticManager.trackEvent("Copy invitation link clicked", this.analyticsPropsMethod())
			let link = this.resolveInviteLink()
			await this.copyToClipboard(link)
		},

		resolveInviteLink() {
			let inviteId = this.programItem.inviteId
			let params = {coachId: this.ownerProfile.id}
			let query = {code: inviteId}
			let link = this.resolvePathByRouteName("AcceptInviteRoute", params, query)
			console.log("resolveInviteLink = ", link)
			return link
		},

		async onInvitationLinkEnableChanged(){
			let _ = this.$analyticManager.trackEvent("Invitation link enable state changed", {value: this.programItem.isLinkInviteEnabled, ...this.analyticsPropsMethod()})
			console.log("onInvitationLinkEnableChanged")
			await this.handleAsync(this.syncInvitationLink(), "Saving...")
		},

		async syncInvitationLink(){
			await Promise.all([
				this.$dataManager.updateProgram(this.programItem),
				this.$dataManager.updateProgramInvite({
					id: this.programItem.inviteId,
					isEnabled: this.programItem.isLinkInviteEnabled
				})
			])
			this.toast("Program updated successfully")
		},

		analyticsPropsMethod(){
			return this.analyticsProps
		}

	}
}
</script>

<style lang="scss">

.program-invite-panel-root {

}

</style>
